/* eslint-disable no-plusplus */
/* eslint-disable no-param-reassign */
import React from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import NumberFormatContext from 'components/CustomInputsInFormikContext/NumberFormat';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useFormikContext } from 'formik';
import { maximumInstallments } from 'utils/consts';
import { roundNumber, skipMonthForEachInstallment } from 'utils';
import {
  filterUnpaidInstallments, mapValueAdjustments, sumOfInstallments, sumOfInstallmentsPaid,
} from '../helpers';

function InstallmentInput({ disabledField }) {
  const {
    setFieldValue, getFieldMeta,
  } = useFormikContext();
  const { value: qtyParts } = getFieldMeta('qtyParts');
  const { value: parts } = getFieldMeta('parts');
  const { value: billValue } = getFieldMeta('value');

  const handleIncrementInstallment = () => {
    if (qtyParts < maximumInstallments) {
      const fixedValue = sumOfInstallmentsPaid(parts);

      const medValue = roundNumber(
        (billValue - fixedValue)
        / (filterUnpaidInstallments(parts).length + 1),
      );

      const mappedArray = mapValueAdjustments(parts, medValue, true);
      const newItemValue = Number((billValue - sumOfInstallments(mappedArray)).toFixed(2));

      const updateParts = [
        ...mappedArray,
        {
          bankAccount: '',
          expirationDate: skipMonthForEachInstallment(new Date(), mappedArray.length),
          part: mappedArray.length,
          pay: false,
          paymentDate: null,
          paymentType: '',
          value: newItemValue,
        },
      ];

      setFieldValue('parts', updateParts);

      setFieldValue('qtyParts', qtyParts + 1);
    }
  };

  const handleDecrementInstallment = () => {
    if (qtyParts <= 1 || parts.length === 1) {
      return;
    }

    const fixedValue = sumOfInstallmentsPaid(parts); // 10
    const unpaidInstallments = filterUnpaidInstallments(parts); // 1 - 1

    let medValue = 0;
    if (unpaidInstallments.length > 1) {
      medValue = roundNumber((billValue - fixedValue) / (unpaidInstallments.length - 1));
    }

    const mappedArray = mapValueAdjustments(parts, medValue);
    mappedArray.pop();

    const newItemValue = Number((billValue - sumOfInstallments(mappedArray)).toFixed(2));
    mappedArray[mappedArray.length - 1].value += newItemValue;

    setFieldValue('qtyParts', qtyParts - 1);
    setFieldValue('parts', [...mappedArray]);
  };

  const handleChangeQtyParts = (value) => {
    if (value === 0) {
      return;
    }

    const currentPartCount = parts.length;
    const desiredPartCount = value;

    if (desiredPartCount < currentPartCount && parts.length > 1) {
      const numberOfItemsToRemove = currentPartCount - desiredPartCount;
      let valueToRedistribute = 0;

      for (let i = 0; i < numberOfItemsToRemove; i++) {
        valueToRedistribute += parts[parts.length - 1].value;
        parts.pop();
      }

      const remainingElements = filterUnpaidInstallments(parts);
      const splitDiff = valueToRedistribute / remainingElements.length;

      remainingElements.forEach((element) => {
        element.value += splitDiff;
        element.value = Number(element.value.toFixed(2));
      });

      const diff = billValue - sumOfInstallments(parts);
      parts[parts.length - 1].value += diff;
      parts[parts.length - 1].value = Number(
        parts[parts.length - 1].value.toFixed(2),
      );
    } else if (desiredPartCount > currentPartCount) {
      const numberOfItemsToAdd = desiredPartCount - currentPartCount;

      const fixedValue = sumOfInstallmentsPaid(parts);

      const medValue = roundNumber(
        (billValue - fixedValue)
          / (filterUnpaidInstallments(parts).length
            + numberOfItemsToAdd),
      );
      const mappedArray = mapValueAdjustments(parts, medValue);

      for (let i = 0; i < numberOfItemsToAdd; i++) {
        mappedArray.push({
          bankAccount: '',
          expirationDate: skipMonthForEachInstallment(new Date(), mappedArray.length),
          part: mappedArray.length,
          pay: false,
          value: Number(medValue.toFixed(2)),
          paymentDate: null,
          paymentType: '',
        });
      }

      const newItemValue = Number((billValue - sumOfInstallments(mappedArray)).toFixed(2));

      if (newItemValue > 0) {
        mappedArray[mappedArray.length - 1].value += newItemValue;
      }
      setFieldValue('parts', mappedArray);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      handleIncrementInstallment();
    } else if (e.key === 'ArrowDown') {
      handleDecrementInstallment();
    }
  };

  return (
    <Box display="flex" alignItems="center" gap="8px">
      <Tooltip title="Remover">
        <IconButton
          sx={{ bgcolor: '#F9DEDC' }}
          disabled={disabledField}
          onClick={handleDecrementInstallment}
        >
          <RemoveIcon sx={{ color: '#B3261E' }} />
        </IconButton>
      </Tooltip>
      <NumberFormatContext
        disabled={disabledField}
        fieldSize={20}
        label="Parcelamento"
        name="qtyParts"
        allowNegative={false}
        centerText
        onValueChangeCallback={(value) => {
          if (value > maximumInstallments) {
            setFieldValue('qtyParts', maximumInstallments);
            return;
          }
          handleChangeQtyParts(value);
        }}
        onKeyDown={handleKeyDown}
      />
      <Tooltip title="Adicionar">
        <IconButton
          sx={{ bgcolor: '#DBE5DE' }}
          disabled={disabledField}
          onClick={handleIncrementInstallment}
        >
          <AddIcon sx={{ color: '#006E2D' }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

export default InstallmentInput;
